/**
 * Option vocabularies for the store-details form, ported from ctrl-saas.
 *
 * Every one of these columns is free text in Postgres, so the selects are a
 * guardrail rather than a constraint: a stored value the list does not carry
 * (an import, a hand-edited row) is appended by `withStoredValue` instead of
 * being silently rewritten on the next save.
 */

export interface Option {
  value: string;
  label: string;
}

/**
 * A curated IANA list -- the zones a North American cannabis retailer
 * plausibly trades in, ordered by offset so the picker reads geographically.
 */
export const TIMEZONE_OPTIONS: Option[] = [
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Vancouver', label: 'Pacific — Vancouver' },
  { value: 'America/Phoenix', label: 'Arizona — Phoenix (no DST)' },
  { value: 'America/Denver', label: 'Mountain — Denver' },
  { value: 'America/Edmonton', label: 'Mountain — Edmonton' },
  { value: 'America/Chicago', label: 'Central — Chicago' },
  { value: 'America/Winnipeg', label: 'Central — Winnipeg' },
  { value: 'America/New_York', label: 'Eastern — New York' },
  { value: 'America/Detroit', label: 'Eastern — Detroit' },
  { value: 'America/Toronto', label: 'Eastern — Toronto' },
  { value: 'America/Halifax', label: 'Atlantic — Halifax' },
  { value: 'America/St_Johns', label: 'Newfoundland — St John’s' },
  { value: 'UTC', label: 'UTC' },
];

export const CURRENCY_OPTIONS: Option[] = [
  { value: 'USD', label: 'USD — US dollar' },
  { value: 'CAD', label: 'CAD — Canadian dollar' },
];

export const WEIGHT_UNIT_OPTIONS: Option[] = [
  { value: 'g', label: 'Grams (g)' },
  { value: 'oz', label: 'Ounces (oz)' },
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'kg', label: 'Kilograms (kg)' },
];

export const COUNTRY_OPTIONS: Option[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
];

export const US_STATE_OPTIONS: Option[] = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
].map((code) => ({ value: code, label: code }));

/**
 * Adds a stored value the curated list does not carry, so an imported timezone
 * or an unusual currency survives a round-trip through the form rather than
 * being replaced by whatever the select happens to show.
 */
export function withStoredValue(options: Option[], stored: string | null): Option[] {
  if (!stored || options.some((option) => option.value === stored)) {
    return options;
  }

  return [...options, { value: stored, label: stored }];
}
